import React, { useEffect, useState } from 'react';
import FusePageSimple from '@fuse/core/FusePageSimple';
import { makeStyles, useTheme, ThemeProvider } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import FuseAnimateGroup from '@fuse/core/FuseAnimateGroup';
import Grid from '@material-ui/core/Grid';
import FuseAnimate from '@fuse/core/FuseAnimate';
import Icon from '@material-ui/core/Icon';
import { useSelector } from 'react-redux';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Button from '@material-ui/core/Button';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { Box } from '@mui/material';
import SortableTree, { changeNodeAtPath, removeNodeAtPath } from 'react-sortable-tree';
import { v4 as uuidv4 } from 'uuid';
import 'react-sortable-tree/style.css';
import 'app/main/pages/company-profile/customerAccountDetails/SideNavigationPages/autoReply/addAutoReply.css';
import { addNodeUnderParent } from 'app/main/pages/company-profile/customerAccountDetails/SideNavigationPages/autoReply/helperfunctions';
import DeleteTreeNode from 'app/main/pages/company-profile/customerAccountDetails/SideNavigationPages/autoReply/DeleteTreeNode';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import SnackbarOmni from 'allGlobalComponents/SnackbarOmni';
import { showSuccessSwal, showErrorSwal } from 'app/utility/Swalutility';
import CallNodeDialog from './CallNodeDialog';
import { createDefaultTree, nodeToIvr, ivrToNode, validateTree, cloneTreeNode, NODE_KINDS } from './ivrHelpers';

const useStyles = makeStyles(theme => ({
	content: {
		'& canvas': {
			maxHeight: '80%'
		}
	},
	chatBotFields: {
		[theme.breakpoints.down('xs')]: {
			flexDirection: 'column-reverse'
		},
		padding: '15px'
	},
	toggleContainer: {
		marginLeft: '30px',
		[theme.breakpoints.down('xs')]: {
			paddingLeft: '0px'
		}
	},
	btn: {
		backgroundColor: theme.palette.secondary.main,
		'&:hover': {
			backgroundColor: theme.palette.secondary.main
		}
	},
	customSwitch: {
		'& .MuiSwitch-switchBase.Mui-checked': {
			color: theme.palette.background.SwitchToggler
		},
		'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
			backgroundColor: theme.palette.background.SwitchToggler
		}
	}
}));

const KIND_LABELS = {
	[NODE_KINDS.MENU]: 'Menu',
	[NODE_KINDS.END]: 'Audio • End',
	[NODE_KINDS.TRANSFER_AGENT]: 'AI Agent',
	[NODE_KINDS.TRANSFER_HUMAN]: 'Human'
};

function AddCallReply(props) {
	const theme = useTheme();
	const mainTheme = useSelector(({ fuse }) => fuse.settings.mainTheme);
	const classes = useStyles();

	const { data, clientId } = props;

	const [treeData, setTreeData] = useState(createDefaultTree());
	const [chatBotName, setChatBotName] = useState('');
	// New chatbots are created disabled by default; editing loads the saved value.
	const [isActive, setIsActive] = useState(false);
	const [copyPasteEnabled, setCopyPasteEnabled] = useState(false);
	const [showTypes, setShowTypes] = useState(false);
	const [openAddModal, setOpenAddModal] = useState(null);
	const [openEditModal, setOpenEditModal] = useState(null);
	const [openDeleteModal, setOpenDeleteModal] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [snackbar, setSnackbar] = useState({ message: '', color: 'success', trigger: 0 });

	const showSnackbar = (message, color = 'error') =>
		setSnackbar(prev => ({ ...prev, message, color, trigger: prev.trigger + 1 }));

	useEffect(() => {
		if (data) {
			const { name, body, active } = data;
			if (name) setChatBotName(name);
			setIsActive(active !== false);
			if (body && typeof body === 'object') {
				setTreeData([ivrToNode(body, undefined, 'Welcome')]);
			}
		}
	}, []);

	const getNodeKey = ({ treeIndex }) => treeIndex;

	const openAddModalDialogue = row => setOpenAddModal(row);
	const openEditModalDialogue = row => setOpenEditModal(row);
	const openDeleteModalDialogue = row => setOpenDeleteModal(row);

	const handleAddDialogClose = () => setOpenAddModal(null);
	const handleEditDialogClose = () => setOpenEditModal(null);
	const handleDeleteDialogClose = () => setOpenDeleteModal(null);

	const addNode = newNode => {
		let parentKey = openAddModal.treeIndex;
		if (parentKey === -1) parentKey = null;

		const newTree = addNodeUnderParent({
			treeData,
			newNode: { ...newNode, id: uuidv4(), expanded: true, children: [] },
			expandParent: true,
			parentKey,
			getNodeKey
		});
		setTreeData(newTree.treeData);
		setOpenAddModal(null);
	};

	const editNode = updated => {
		const { path, node } = openEditModal;
		const newTree = changeNodeAtPath({
			treeData,
			path,
			newNode: { ...node, ...updated },
			getNodeKey,
			ignoreCollapsed: true
		});
		setTreeData(newTree);
		setOpenEditModal(null);
	};

	const deleteNode = () => {
		const { path } = openDeleteModal;
		const newTree = removeNodeAtPath({ treeData, path, getNodeKey, ignoreCollapsed: true });
		setTreeData(newTree);
		setOpenDeleteModal(null);
	};

	// Insert a clone of `rowInfo.node` as a sibling (under the same parent).
	// withChildren copies the whole sub-tree (e.g. duplicate a full language
	// menu); otherwise only the node itself is copied ("Copy Parent").
	// The copied node's key is left blank so it never collides with the original
	// (keys must be unique among siblings); the user assigns a new key after.
	const cloneNode = (rowInfo, withChildren) => {
		const parentPath = rowInfo.path.slice(0, -1);
		const parentKey = parentPath.length ? parentPath[parentPath.length - 1] : null;
		const clonedNode = { ...cloneTreeNode(rowInfo.node, withChildren), optionKey: '' };
		const newTree = addNodeUnderParent({
			treeData,
			newNode: clonedNode,
			expandParent: true,
			parentKey,
			getNodeKey
		});
		setTreeData(newTree.treeData);
	};

	// Keys already used by the target node's siblings, so the dialog can reject
	// duplicates. For "add", the target is the parent (all its children count);
	// for "edit", the node's own key is excluded.
	const siblingKeysForAdd = parentRowInfo =>
		(parentRowInfo?.node?.children || [])
			.map(c => (c.optionKey == null ? '' : String(c.optionKey)))
			.filter(k => k !== '');

	const siblingKeysForEdit = rowInfo =>
		(rowInfo?.parentNode?.children || [])
			.filter(c => c.id !== rowInfo?.node?.id)
			.map(c => (c.optionKey == null ? '' : String(c.optionKey)))
			.filter(k => k !== '');

	// Copy & paste the whole flow using the call chatbot API body shape, so the
	// clipboard content is portable across chatbots.
	const copyFlow = () => {
		const body = nodeToIvr(treeData[0], true);
		navigator.clipboard
			.writeText(JSON.stringify(body))
			.then(() => showSuccessSwal('Copied', 'Chatbot flow copied to clipboard.'))
			.catch(() => showErrorSwal('Error', 'Failed to copy the chatbot flow.'));
	};

	const pasteFlow = () => {
		navigator.clipboard
			.readText()
			.then(text => {
				let body;
				try {
					body = JSON.parse(text);
				} catch (e) {
					showErrorSwal('Error', 'Clipboard does not contain a valid chatbot flow.');
					return;
				}
				if (!body || typeof body !== 'object' || Array.isArray(body)) {
					showErrorSwal('Error', 'Clipboard does not contain a valid chatbot flow.');
					return;
				}
				setTreeData([ivrToNode(body, undefined, 'Welcome')]);
				showSuccessSwal('Pasted', 'Chatbot flow pasted from clipboard.');
			})
			.catch(() => showErrorSwal('Error', 'Failed to read from clipboard.'));
	};

	function closeHandler() {
		props.closeHandler({});
	}

	function saveHandler() {
		if (!chatBotName) {
			showSnackbar('Please enter a chatbot name.');
			return;
		}

		const rootNode = treeData[0];
		const validationErrors = validateTree(rootNode);
		if (validationErrors.length) {
			showSnackbar(validationErrors[0]);
			return;
		}

		const ivrMenu = nodeToIvr(rootNode, true);

		const params = {
			name: chatBotName,
			body: ivrMenu,
			active: isActive
		};

		setIsLoading(true);
		if (data) {
			props.updateHandler(params);
		} else {
			props.saveHandler(params);
		}
	}

	const canAddChild = node => (node.nodeKind || NODE_KINDS.MENU) === NODE_KINDS.MENU;

	// Human-readable "type" for the Show Types badge. Adds the provider/agent
	// detail for transfer nodes, mirroring the autoReply "type > subtype" chip.
	const nodeTypeLabel = node => {
		const kind = node.nodeKind || NODE_KINDS.MENU;
		let label = KIND_LABELS[kind] || 'Menu';
		if (kind === NODE_KINDS.TRANSFER_AGENT && node.agentType) label += ` › ${node.agentType}`;
		else if (kind === NODE_KINDS.TRANSFER_HUMAN && node.agentId) label += ` › Agent #${node.agentId}`;
		return label;
	};

	return (
		<FusePageSimple
			classes={{
				header: 'min-h-50 h-50 sm:h-50 sm:min-h-50 autoReplyHeader',
				content: classes.content
			}}
			header={
				<div className="flex flex-col justify-between flex-1 pl-20">
					<div className="flex items-center">
						<FuseAnimate animation="transition.expandIn" delay={300}>
							<Icon className="text-26">phone_in_talk</Icon>
						</FuseAnimate>
						<FuseAnimate animation="transition.slideLeftIn" delay={300}>
							<Typography className="py-0 sm:py-24 hidden sm:flex mx-0 sm:mx-12 text-20" variant="h6">
								Chatbot Calls
							</Typography>
						</FuseAnimate>
						<div className="flex flex-1 items-end justify-end px-12">
							<ThemeProvider theme={mainTheme}>
								<FuseAnimate animation="transition.slideDownIn" delay={300}>
									<React.Fragment>
										<ButtonOmni
											onClick={closeHandler}
											size="small"
											label="Cancel"
											variant="contained"
											style={{ marginRight: '10px' }}
										/>
										<ButtonOmni
											variant="contained"
											onClick={saveHandler}
											label={data ? 'Update' : 'Save'}
											size="small"
											loading={isLoading}
										/>
									</React.Fragment>
								</FuseAnimate>
							</ThemeProvider>
						</div>
					</div>
				</div>
			}
			content={
				<div style={{ background: theme.palette.background.paper, paddingTop: '20px' }} className="w-75">
					<Grid container spacing={4} className={classes.chatBotFields}>
						<Grid item md={9} sm={12} xs={12}>
							<Grid container className="mb-20">
								<Grid item md={6} sm={12} xs={12}>
									<div className="flex">
										<TextField
											fullWidth
											label="Chat Bot Name"
											autoFocus
											required
											name="chatBotName"
											variant="outlined"
											value={chatBotName}
											onChange={e => setChatBotName(e.target.value)}
											size="small"
											inputProps={{ maxLength: 30 }}
										/>
									</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item md={3} sm={12} xs={12}>
							<div className={classes.toggleContainer}>
								<FormControlLabel
									control={
										<Switch
											checked={copyPasteEnabled}
											onChange={e => setCopyPasteEnabled(e.target.checked)}
											name="copyPaste"
											className={classes.customSwitch}
											size="small"
										/>
									}
									label="Copy & Paste Chatbot"
								/>
								<Grid item md={12} sm={12} xs={12}>
									<FormControlLabel
										control={
											<Switch
												checked={showTypes}
												onChange={e => setShowTypes(e.target.checked)}
												name="showTypes"
												className={classes.customSwitch}
												size="small"
											/>
										}
										label="Show Types"
									/>
								</Grid>
								{copyPasteEnabled && (
									<Grid item md={12} sm={12} xs={12}>
										<FormControlLabel
											control={
												<div style={{ margin: '15%' }}>
													<Tooltip title="Copy" arrow>
														<Button
															className={classes.btn}
															color="secondary"
															variant="contained"
															onClick={copyFlow}
														>
															<ContentCopyIcon />
														</Button>
													</Tooltip>
												</div>
											}
										/>
										<FormControlLabel
											control={
												<div style={{ margin: '15%' }}>
													<Tooltip title="Paste" arrow>
														<Button
															className={classes.btn}
															color="secondary"
															variant="contained"
															onClick={pasteFlow}
														>
															<ContentPasteIcon />
														</Button>
													</Tooltip>
												</div>
											}
										/>
									</Grid>
								)}
							</div>
						</Grid>
					</Grid>

					<div className="p-24" style={{ paddingTop: '0px' }}>
						<FuseAnimateGroup className="flex flex-wrap" enter={{ animation: 'transition.slideUpBigIn' }}>
							<Grid container spacing={3} style={{ marginTop: 10 }}>
								<Grid item md={12} sm={12} xs={12}>
									<SortableTree
										treeData={treeData}
										isVirtualized={false}
										scaffoldBlockPxWidth={50}
										className={
											theme.palette.type === 'dark'
												? 'sortable-tree-container-dark'
												: 'sortable-tree-container-light'
										}
										generateNodeProps={rowInfo => ({
											title: (
												<span style={{ fontSize: '15px', fontWeight: 600 }}>
													{rowInfo.node.title}
												</span>
											),
											subtitle: null,
											buttons: [
												<div className="chatBotContainer">
													<Box
														className="chatBotIconContainer"
														sx={{
															alignItems: 'center',
															paddingRight: '20px',
															'& > *': { margin: '3px !important' }
														}}
													>
														{canAddChild(rowInfo.node) ? (
															<Tooltip title="Add Option" arrow>
																<Icon
																	className="chatBotButtons"
																	onClick={() => openAddModalDialogue(rowInfo)}
																	style={{ color: theme.palette.secondary.value }}
																>
																	add
																</Icon>
															</Tooltip>
														) : null}
														<Tooltip title="Edit Node" arrow>
															<Icon
																className="chatBotButtons"
																onClick={() => openEditModalDialogue(rowInfo)}
																style={{ color: theme.palette.secondary.value }}
															>
																edit
															</Icon>
														</Tooltip>
														{rowInfo.path.length > 1 ? (
															<Tooltip title="Delete Node" arrow>
																<Icon
																	className="chatBotButtons"
																	onClick={() => openDeleteModalDialogue(rowInfo)}
																	style={{ color: theme.palette.secondary.value }}
																>
																	delete
																</Icon>
															</Tooltip>
														) : null}
														{rowInfo.path.length > 1 ? (
															<Tooltip title="Copy option with its sub-options" arrow>
																<Icon
																	className="chatBotButtons"
																	onClick={() => cloneNode(rowInfo, true)}
																	style={{ color: theme.palette.secondary.value }}
																>
																	arrow_downward
																</Icon>
															</Tooltip>
														) : null}
														{rowInfo.path.length > 1 &&
														canAddChild(rowInfo.node) &&
														(rowInfo.node.children || []).length > 0 ? (
															<Tooltip title="Copy option only (without sub-options)" arrow>
																<Icon
																	className="chatBotButtons"
																	onClick={() => cloneNode(rowInfo, false)}
																	style={{ color: '#ff6b6b' }}
																>
																	content_copy
																</Icon>
															</Tooltip>
														) : null}
													</Box>
													{rowInfo.path.length > 1 &&
													rowInfo.node.optionKey != null &&
													rowInfo.node.optionKey !== '' ? (
														<div className="chatBotKey">
															<span className="chatBotTreeKey">{rowInfo.node.optionKey}</span>
														</div>
													) : null}
													{showTypes ? (
														<div
															className="chatBotKey"
															style={{
																color: 'white',
																display: 'flex',
																alignItems: 'center',
																fontSize: '12px',
																whiteSpace: 'nowrap',
																background: '#1b998b',
																padding: '0 8px',
																height: '100%'
															}}
														>
															{nodeTypeLabel(rowInfo.node)}
														</div>
													) : null}
												</div>
											],
											className: 'chatBotTree-base-icon chatBotTree-' + (rowInfo.node.nodeKind || 'menu')
										})}
										onChange={td => setTreeData(td)}
										canDrop={({ nextParent, nextPath }) =>
											nextPath.length > 1 &&
											(!nextParent || (nextParent.nodeKind || NODE_KINDS.MENU) === NODE_KINDS.MENU)
										}
									/>
								</Grid>
							</Grid>

							{openAddModal && (
								<CallNodeDialog
									isAdd
									isRoot={false}
									clientId={clientId}
									siblingKeys={siblingKeysForAdd(openAddModal)}
									closeDialog={handleAddDialogClose}
									submitNode={addNode}
								/>
							)}
							{openEditModal && (
								<CallNodeDialog
									isRoot={openEditModal.path.length === 1}
									initialNode={openEditModal.node}
									clientId={clientId}
									siblingKeys={siblingKeysForEdit(openEditModal)}
									closeDialog={handleEditDialogClose}
									submitNode={editNode}
								/>
							)}
							{openDeleteModal && (
								<DeleteTreeNode
									deleteTreeNode={openDeleteModal}
									closeDialog={handleDeleteDialogClose}
									isOpen={!!openDeleteModal}
									submitNode={deleteNode}
								/>
							)}
						</FuseAnimateGroup>
					</div>
					<SnackbarOmni message={snackbar.message} color={snackbar.color} trigger={snackbar.trigger} />
				</div>
			}
		/>
	);
}

export default AddCallReply;
